import React, { useState } from "react";
import Icon from "../Icon";
import { FormattedUpload } from "../types";

interface Props {
  record: FormattedUpload;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

const ReverseConfirmationModal: React.FC<Props> = ({
  record,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const body =
    record.status === "processing"
      ? "Reconciliation is currently running. The job will finish cleanly, then this reconciliation will be reversed. The recon entries it produces will be marked reversed when the job completes. After that, you can upload a new file for the same products and value dates."
      : "Reversing will mark every reconciliation entry from this upload as reversed. They stay in the audit trail (visible via the 'Show reversed' filter) but won't count toward Phoenix's source-of-truth view. After reversal, you can upload a new file for the same products and value dates.";

  const confirm = (): void => {
    if (submitting) return;
    setSubmitting(true);
    onConfirm(reason.trim());
  };

  return (
    <>
      <div
        className="drawer-backdrop open"
        style={{ zIndex: 60 }}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          inset: 0,
          display: "grid",
          placeItems: "center",
          zIndex: 61,
        }}
      >
        <div
          style={{
            background: "var(--surface)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-lg)",
            width: 520,
            maxWidth: "calc(100vw - 32px)",
            padding: 24,
            border: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
            }}
          >
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "var(--danger-bg)",
                color: "var(--danger)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Icon name="alert-triangle" size={16} />
            </span>
            <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
              Reverse {record.id}?
            </h3>
          </div>
          <p style={{ color: "var(--muted)", marginTop: 4, lineHeight: 1.5 }}>
            {body}
          </p>

          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--ink-2)",
              marginTop: 14,
              marginBottom: 6,
            }}
          >
            Why are you reversing this? (optional)
          </label>
          <textarea
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="TA published corrections, wrong period, …"
            style={{
              width: "100%",
              padding: 10,
              border: "1px solid var(--border-strong)",
              borderRadius: "var(--radius)",
              font: "inherit",
              resize: "vertical",
              minHeight: 60,
            }}
          />
          <div
            style={{
              fontSize: 11,
              color: "var(--muted)",
              marginTop: 4,
              textAlign: "right",
            }}
          >
            {reason.length}/500
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 18,
              justifyContent: "flex-end",
            }}
          >
            <button className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              className="btn btn-danger-ghost"
              onClick={confirm}
              disabled={submitting}
              style={{
                background: "var(--danger)",
                color: "#fff",
                border: "1px solid var(--danger)",
              }}
            >
              <Icon name="alert-triangle" size={13} strokeWidth={2.5} /> Reverse
              reconciliation
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReverseConfirmationModal;
